<template>
	<component
		:is="themeComponent"
		:theme="theme"
		:data-test="`common-theme-${selectedVertical}`"
		:class="[$style.genericTheme, withBackground && $style.withBackground]"
		v-bind="$attrs"
	>
		<slot />
	</component>
</template>

<script lang="ts" setup>
import type { Vertical } from '../../types/vertical';
import type { Theme } from '../../types/theme';

interface Props {
	theme?: Theme;
	withBackground?: boolean;
	vertical?: Vertical;
}

const props = withDefaults(defineProps<Props>(), {
	theme: 'Regular',
	withBackground: true,
	vertical: undefined,
});

const initialVertical = computed((): Vertical => props.vertical || 'portal');

if (props.vertical) {
	useProvideVerticalTheme(initialVertical);
}

const selectedVertical = computed((): Vertical => props.vertical || useCurrentVerticalTheme()?.value || 'portal');

const themeComponent = computed(() => {
	switch (selectedVertical.value) {
		case 'eurojackpot':
			return resolveComponent('LazyEjpCommonTheme');
		case 'lotto':
			return resolveComponent('LazyLottoCommonTheme');
		case 'luckyday':
			return resolveComponent('LazyLcdCommonTheme');
		case 'portal':
			return resolveComponent('LazyPortalCommonTheme');
		case 'staatsloterij':
			return resolveComponent('LazyStlCommonTheme');
		case 'krasloten':
			return resolveComponent('LazyKlCommonTheme');
		case 'miljoenenspel':
			return resolveComponent('LazyMjsCommonTheme');
		case 'totowinkel':
			return resolveComponent('LazyTotoCommonTheme');
		default:
			throw new Error(`Unknown vertical: ${selectedVertical.value}`);
	}
});

defineOptions({
	inheritAttrs: false,
});
</script>

<style lang="scss" module>
.genericTheme {
	// Generic global setters
	color: var(--surface--on-surface);
	font-family: var(--typography--font-family-body);
}

.withBackground {
	background: var(--surface--surface);
}
</style>
